<template>
  <div>
    <div class="progress-steps-indicator-container">
      <progress-steps
        v-if="displaySteps"
        :current-step="getCurrentStep()"
        :is-completed="isCompleted()"
        :is-failed="isFailed()"
        :steps="getDocumentProcessingSteps()"
        @dropdown-update="handleDocumentDataChanged"
      >
      </progress-steps>
      <p v-else-if="isSharePointUploadFailed" class="document-error">
        {{ $t('progressStepsIndicator.documentUploadFailed') }}
      </p>
      <p v-else-if="isTimedOut()" class="document-error">
        {{ $t('progressStepsIndicator.timedOut') }}
      </p>
    </div>
  </div>
</template>

<script setup lang="ts">
  import { computed } from 'vue';
  import { useI18n } from 'vue-i18n';
  import {
    DigiDocuProcessingStep,
    getCurrentDigitalizationDocumentProcessingStep,
  } from '@/helpers/getCurrentDigitalizationDocumentProcessingStep';
  import {
    DocuProcessingStep,
    getCurrentDocumentProcessingStep,
    isStepFailed as isSupplierDocumentStepFailed,
  } from '@/helpers/getCurrentDocumentProcessingStep';
  import { isStepFailed as isDigitalizationDocumentStepFailed } from '@/helpers/getCurrentDigitalizationDocumentProcessingStep';
  import {
    DocumentStatus,
    IDigitalizationDocumentDetails,
    ISupplierDocumentDetails,
    ProjectType,
    SupplierDocumentStatus,
  } from 'types';
  import { getEnumLength } from 'utils';
  import { Step } from '../ProgressSteps/ProgressSteps.types';
  import { hideAllPoppers } from 'floating-vue';
  import DocumentStatusDropdown from '@/components/DocumentStatusDropdown/DocumentStatusDropdown.vue';
  import { DocumentStatusDropdownProps } from '../DocumentStatusDropdown/DocumentStatusDropdown.types';

  const props = defineProps<{
    mode: ProjectType;
    file?: ISupplierDocumentDetails;
    digitalizationDocument?: IDigitalizationDocumentDetails;
  }>();

  const { t } = useI18n();

  const emit = defineEmits<{
    (e: 'document-data-changed'): void;
  }>();

  const displaySteps = computed(
    () => !isSharePointUploadFailed.value && !isTimedOut(),
  );

  const isSharePointUploadFailed = computed(
    () =>
      props.file?.didSharePointUploadFail ||
      props.digitalizationDocument?.didSharePointUploadFail,
  );

  function isTimedOut(): boolean {
    if (!props.file) return false;
    return (
      props.file.documentProcessingStatus === SupplierDocumentStatus.TIMED_OUT
    );
  }

  function getDocumentProcessingSteps(): Step<DocumentStatusDropdownProps>[] {
    if (props.mode === ProjectType.SUPPLIER_PROJECT) {
      return buildDocumentProcessingSteps(
        DocuProcessingStep,
        ProjectType.SUPPLIER_PROJECT,
      );
    } else {
      return buildDocumentProcessingSteps(
        DigiDocuProcessingStep,
        ProjectType.DIGITALIZATION_PROJECT,
      );
    }
  }

  function buildDocumentProcessingSteps(
    stepEnum: typeof DocuProcessingStep | typeof DigiDocuProcessingStep,
    projectType: ProjectType,
  ): Step<DocumentStatusDropdownProps>[] {
    return Array.from({
      length: getEnumLength(stepEnum),
    }).map((_, index) => ({
      label: t(`progressStepsIndicator.${projectType}.${index}`),
      value: index,
      displayValue: index + 1,
      dropdownComponent: getUserActionableStepIndexes().includes(index)
        ? {
            component: DocumentStatusDropdown,
            props: {
              digitalizationDocument: props.digitalizationDocument,
              file: props.file,
              mode: props.mode,
            },
          }
        : undefined,
    }));
  }

  function getCurrentStep(): number {
    if (props.mode === ProjectType.SUPPLIER_PROJECT && props.file) {
      return getCurrentDocumentProcessingStep(props.file);
    } else if (
      props.mode === ProjectType.DIGITALIZATION_PROJECT &&
      props.digitalizationDocument
    ) {
      return getCurrentDigitalizationDocumentProcessingStep(
        props.digitalizationDocument,
      );
    } else return 0;
  }

  function isFailed(): boolean {
    if (props.mode === ProjectType.SUPPLIER_PROJECT && props.file) {
      return isSupplierDocumentStepFailed(props.file.documentProcessingStatus);
    } else if (
      props.mode === ProjectType.DIGITALIZATION_PROJECT &&
      props.digitalizationDocument
    ) {
      return isDigitalizationDocumentStepFailed(
        props.digitalizationDocument.documentProcessingStatus,
      );
    } else return false;
  }

  function isCompleted(): boolean {
    if (props.mode === ProjectType.SUPPLIER_PROJECT && props.file) {
      return (
        props.file.documentProcessingStatus === SupplierDocumentStatus.COMPLETED
      );
    } else if (
      props.mode === ProjectType.DIGITALIZATION_PROJECT &&
      props.digitalizationDocument
    ) {
      return (
        props.digitalizationDocument.documentProcessingStatus ===
        DocumentStatus.RECORD_LIBRARY_TRANSFER_SUCCESSFULL
      );
    }
    return false;
  }

  function getUserActionableStepIndexes(): number[] {
    if (props.mode === ProjectType.SUPPLIER_PROJECT) {
      return [DocuProcessingStep.CONTENT_INSPECTION];
    } else {
      return [
        DigiDocuProcessingStep.METADATA_INSPECTION,
        DigiDocuProcessingStep.QUALITY_CHECK,
      ];
    }
  }

  function handleDocumentDataChanged(): void {
    hideAllPoppers();
    emit('document-data-changed');
  }
</script>

<style lang="scss" scoped>
  .progress-steps-indicator-container {
    width: 100%;
    padding: 0 5%;
  }

  .document-error {
    color: red;
    text-align: center;
    font-weight: 600;
    margin-bottom: 1rem;
  }
</style>
